import { useFieldArray, type UseFormReturn } from "react-hook-form";
import {
  Input,
  FieldError,
  Label,
  Legend,
  RepeaterFields,
  RepeaterField,
  TextArea,
  Fieldset,
  RequiredText,
  AppError,
} from "atoms";
import { ImageUpload } from "features/images";
import { fieldOptions, validators } from "utils/form";
import type { ArtistFormFields } from "../types";

type ArtistCollectionInputProps = Pick<
  UseFormReturn<ArtistFormFields>,
  "register" | "control"
> & {
  errors?: UseFormReturn<ArtistFormFields>["formState"]["errors"];
  maxCollections: number;
};

function ArtistCollectionsInput({
  control,
  errors,
  register,
  maxCollections,
  ...props
}: ArtistCollectionInputProps) {
  const { fields, append, move, remove } = useFieldArray({
    control,
    name: "artistCollections",
  });

  const handleAppend = () =>
    append({ collectionName: "", collectionLinks: [] });

  // Do not allow append if over the max
  const showAppend = fields.length < maxCollections;

  return (
    <Label as="fieldset" {...props}>
      <Legend size="sm">Portfolio</Legend>

      <RepeaterFields
        handleAppend={handleAppend}
        rowLabel="Collection"
        showAppend={showAppend}
      >
        {fields.map((item, index) => (
          <RepeaterField
            key={item.id}
            order={index}
            rowLabel="Collection"
            {...RepeaterField.createHandlers(
              index,
              fields.length,
              move,
              remove
            )}
          >
            <Label>
              Collection Name
              <RequiredText />
              <Input
                {...register(`artistCollections.${index}.collectionName`, {
                  ...fieldOptions.required,
                })}
                isInvalid={!!errors?.artistCollections?.[index]?.collectionName}
              />
              {errors?.artistCollections?.[index]?.collectionName && (
                <FieldError>
                  {errors?.artistCollections?.[index]?.collectionName?.message}
                </FieldError>
              )}
            </Label>

            <fieldset>
              <Legend>
                Thumbnail
                <RequiredText />
              </Legend>

              <ImageUpload
                control={control}
                fieldName={`artistCollections.${index}.collectionImage`}
                required
                aspect="square"
                entityType="PROJECT"
                errorMessage={
                  errors?.artistCollections?.[index]?.collectionImage?.url
                    ?.message
                }
                src={item.collectionImage}
              />
            </fieldset>

            <Label>
              Description
              <br />
              <small>
                Only the first 90-110 characters will be displayed on your
                profile.
              </small>
              <TextArea
                {...register(
                  `artistCollections.${index}.collectionDescription`
                )}
              />
            </Label>

            <Fieldset {...props}>
              <Label>
                Link to Artwork
                <br />
                <small>
                  Share a link for others to see or purchase this artwork.
                </small>
                <Input
                  {...register(
                    `artistCollections.${index}.collectionLinks.0.url`,
                    {
                      validate: validators.validURL,
                    }
                  )}
                />
                {errors?.artistCollections?.[index]?.collectionLinks?.[0]
                  ?.url && (
                  <FieldError block>
                    {
                      errors?.artistCollections?.[index]?.collectionLinks?.[0]
                        ?.url?.message
                    }
                  </FieldError>
                )}
              </Label>
            </Fieldset>
          </RepeaterField>
        ))}
      </RepeaterFields>

      {!showAppend && (
        <AppError>
          You have reached the maximum number of permitted collections.
        </AppError>
      )}
    </Label>
  );
}

export { ArtistCollectionsInput };
